/**
 * Visit gaps, shift activity and tracking copy (Arabic). `travel`, `activity`
 * and `tracking` are carried verbatim from the previous app; their nav labels
 * moved to `nav.*` like every other page's.
 */
export const trackingAr = {
  travel: {
    title: 'الوقت والمسافة بين الزيارات',
    description:
      'لكل مندوب في كل يوم: الدقايق بين الخروج من فرع والتسجيل في الفرع اللي بعده، والمسافة بخط مستقيم بين الفرعين. الخط المستقيم مش مسافة الطريق الفعلية.',
    straightLine: 'المسافة بخط مستقيم',
    straightLineNote: 'المسافة بخط مستقيم بين الفرعين (مش مسافة الطريق الفعلية).',
    gap: 'الفاصل',
    gapMinutes: 'الفاصل (دقيقة)',
    totalGap: 'إجمالي الفواصل',
    avgGap: 'متوسط الفاصل',
    avgDistance: 'متوسط المسافة بخط مستقيم',
    avgVisits: 'زيارات في اليوم',
    pairs: 'زيارات متتالية',
    // The CSV keeps the previous app's column header.
    pairsCsv: 'أزواج',
    unknownDistance: 'المسافة غير معروفة',
    unknownGap: 'الفاصل غير معروف',
    firstVisit: 'أول زيارة في اليوم',
    byAgentDay: 'لكل مندوب في كل يوم',
    byAgent: 'لكل مندوب في الفترة',
    days: 'الأيام',
    visits: 'الزيارات',
    detailTitle: 'زيارات {{name}} يوم {{date}}',
    recompute: 'إعادة الحساب',
    recomputed: 'اتعاد حساب {{count}} يوم',
    recomputed_one: 'اتعاد حساب يوم واحد',
    recomputed_two: 'اتعاد حساب يومين',
    recomputed_few: 'اتعاد حساب {{count}} أيام',
    checkIn: 'الدخول',
    checkOut: 'الخروج',
    flags: {
      NO_END_TIME: 'الزيارة اللي قبلها مالهاش وقت انتهاء',
      OVERLAP: 'متداخلة مع الزيارة اللي قبلها بـ {{minutes}} دقيقة',
      INVERTED_TIMES: 'وقت الانتهاء قبل وقت البداية (ساعة الموبايل)',
      NO_COORDINATES: 'فرع من الاتنين مالوش موقع مؤكد: المسافة غير معروفة',
    },
  },
  activity: {
    title: 'الشيفتات والكيلومترات المقطوعة والتغطية',
    description:
      'لكل مندوب في كل يوم، من نقاط تتبع الشيفت: ساعات الشيفت، والزيارات، والكيلومترات المقطوعة، ونسبة وقت الشيفت اللي عليها نقاط موقع فعلاً. يوم تغطيته قليلة مينفعش يتقارن بيوم كامل.',
    shiftHours: 'الشيفت',
    walked: 'المسافة المقطوعة',
    // The CSV and Excel keep the previous app's column header.
    walkedExport: 'المسافة المقطوعة',
    coverage: 'التغطية',
    lowCoverage: 'تغطية قليلة: مش قابل للمقارنة بيوم كامل',
    noTracking: 'مفيش تتبع للشيفت',
    trackedDays: 'أيام فيها تتبع',
    avgCoverage: 'متوسط التغطية',
    rangeTotal: 'المسافة المقطوعة في الفترة', // new — needs native review
    route: 'المسار',
    kmUnit: 'كم',
    hoursUnit: 'س',
  },
  tracking: {
    liveTitle: 'الخريطة المباشرة',
    liveDescription:
      'المناديب اللي شيفتهم مفتوح. كل نقطة على الخريطة بتوضح من قد إيه اتاخدت آخر نقطة ودائرة دقتها. بتتحدث كل {{seconds}} ثانية. كل مرة حد بيفتحها بتتسجل في سجل المراجعة.',
    routeTitle: 'مسار اليوم',
    routeDescription: 'مندوب واحد في يوم واحد: المسار، والوقت في كل فرع، وفترات الوقوف، والانقطاعات في التتبع وسببها المحتمل.',
    states: {
      LIVE: 'مباشر',
      STALE: 'قديم',
      SHIFT_ENDED: 'الشيفت انتهى',
      CONSENT_MISSING: 'مفيش موافقة على التتبع',
      PERMISSION_DENIED: 'إذن الموقع مرفوض',
      NO_POINT_YET: 'لسه مفيش نقطة',
    },
    since: 'من {{time}}',
    accuracy: 'الدقة',
    battery: 'البطارية',
    mocked: 'موقع وهمي',
    // New copy (UX audit #7): needs native review.
    legend: 'مفتاح الخريطة',
    accuracyCircle: 'دقة الموقع (GPS)',
    lastPoint: 'آخر نقطة',
    shiftStarted: 'بداية الشيفت',
    noAgents: 'مفيش مناديب.',
    timeline: 'التسلسل الزمني',
    timelineKinds: { SHIFT_START: 'بدء الشيفت', SHIFT_END: 'إنهاء الشيفت', VISIT: 'زيارة', IDLE: 'وقوف', GAP: 'انقطاع في التتبع' },
    /** Not in the previous app (its timeline showed the server's Arabic text): the shift's end reason code, in words. */
    endReasons: {
      AGENT: 'المندوب أنهى الشيفت',
      AUTO_STOP: 'اتقفل تلقائياً في الميعاد المحدد',
      SERVER_AUTO_CLOSE: 'السيرفر قفله تلقائياً',
      CONSENT_WITHDRAWN: 'المندوب سحب الموافقة على التتبع',
    },
    gapReasons: {
      APP_CLOSED: 'التطبيق اتقفل أو الموبايل وقّفه',
      PERMISSION_REVOKED: 'إذن الموقع اتسحب',
      LOCATION_OFF: 'خدمة الموقع مقفولة',
      BATTERY_SAVER: 'توفير البطارية شغال',
      NO_SIGNAL: 'مفيش إشارة موقع',
    },
    pointsPurged: 'النقاط التفصيلية لليوم ده اتمسحت بعد مدة الاحتفاظ. ملخص اليوم محفوظ.',
    points: 'النقاط',
    idle: 'فترات الوقوف',
    gaps: 'الانقطاعات في التتبع',
    timeAtBranches: 'الوقت في الفروع',
    chooseAgent: 'اختار مندوب ويوم.',
    settings: 'إعدادات التتبع',
    settingsHint: 'الموبايلات بتطبقها مع أول فتح للتطبيق أو أول شيفت. التعديل للمدير العام بس.',
    retention: 'مدة الاحتفاظ بالنقاط (يوم)',
    retentionNote: 'نقاط الموقع هي البيانات الوحيدة في النظام اللي ليها مدة احتفاظ: بتتمسح بعد {{days}} يوم. ملخصات الأيام محفوظة على طول.',
    oldestPoint: 'أقدم نقطة محفوظة: {{date}} ({{count}} نقطة)',
    oldestPoint_one: 'أقدم نقطة محفوظة: {{date}} (نقطة واحدة)',
    oldestPoint_two: 'أقدم نقطة محفوظة: {{date}} (نقطتين)',
    oldestPoint_few: 'أقدم نقطة محفوظة: {{date}} ({{count}} نقاط)',
    sampleMin: 'قراءة كل (ثانية)',
    sampleMax: 'نقطة على الأقل كل (ثانية)',
    sampleDistance: 'أو بعد الحركة (متر)',
    accuracyMode: 'الدقة',
    accuracyModes: { BALANCED: 'متوازنة (توفّر البطارية)', HIGH: 'عالية (GPS)' },
    uploadInterval: 'الرفع كل (ثانية)',
    uploadBatch: 'أو بعد عدد النقاط ده',
    reminderTime: 'تذكير لو الشيفت لسه مفتوح الساعة',
    autoStop: 'الشيفت بيقفل لوحده الساعة',
    maxAccuracy: 'الكيلومترات: تجاهل النقاط اللي دقتها أسوأ من (متر)',
    minMovement: 'الكيلومترات: تجاهل الحركة الأقل من (متر)',
    maxSpeed: 'الكيلومترات: تجاهل القفزات الأسرع من (كم/س)',
    stale: 'الخريطة المباشرة: قديم بعد (ثانية)',
    poll: 'تحديث الخريطة المباشرة (ثانية)',
    gapThreshold: 'انقطاع في التتبع بعد (دقيقة)',
  },
};
